// frontend/src/components/subscription/SubscriptionHandler.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'react-feather';
import SubscriptionService from '../../services/subscription.service';

interface SubscriptionHandlerProps {
  isAuthenticated: boolean;
  planType: 'basic' | 'premium' | 'ultimate';
  planDuration: 'monthly' | 'yearly';
  currency?: string; // Add optional currency prop
}

const SubscriptionHandler: React.FC<SubscriptionHandlerProps> = ({
  isAuthenticated,
  planType,
  planDuration,
  currency = 'NGN' // Default to NGN if not specified
}) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubscription = async () => {
    try {
      setLoading(true);

      // If not authenticated, redirect to register with plan info
      if (!isAuthenticated) {
        // Store plan info including currency in localStorage before redirecting
        localStorage.setItem('selectedPlanType', planType);
        localStorage.setItem('selectedPlanDuration', planDuration);
        localStorage.setItem('selectedCurrency', currency);
        
        navigate(`/register?plan=${planType}&duration=${planDuration}&currency=${currency}`);
        return;
      }

      // For free (basic) plan
      if (planType === 'basic') {
        await SubscriptionService.upgrade('basic', 'monthly'); // Duration doesn't matter for basic
        navigate('/admin/dashboard');
        return;
      }

      // For premium and ultimate plans, initialize payment with currency
      const response = await SubscriptionService.upgrade(planType, planDuration, currency);

      // Redirect to Paystack payment page
      if (response.data && response.data.authorizationUrl) {
        window.location.href = response.data.authorizationUrl;
      } else {
        throw new Error('Failed to initialize payment');
      }
    } catch (error) {
      console.error('Subscription error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get button text based on plan type
  const getButtonText = () => {
    if (loading) return 'Processing...';

    switch (planType) {
      case 'basic':
        return 'Start for Free';
      case 'premium':
        return 'Choose Premium';
      case 'ultimate':
        return 'Choose Ultimate';
      default:
        return 'Select Plan';
    }
  };

  // Style the button based on plan type
  const getButtonStyle = () => {
    if (planType === 'premium') {
      return 'bg-[#FF6B6B] hover:bg-[#FF6B6B]/90 text-white';
    } else if (planType === 'ultimate') {
      return 'bg-[#2ECC71] hover:bg-[#2ECC71]/90 text-white';
    } else {
      return 'bg-gray-100 hover:bg-gray-200 text-gray-800';
    }
  };

  // Special styling for the main CTA button in testimonial section
  const isMainCTA = planType === 'premium' && !planDuration;

  return (
    <button
      onClick={handleSubscription}
      disabled={loading}
      className={`w-full ${isMainCTA ? 'py-4' : 'py-3'} px-6 rounded-full font-medium text-center transition-all flex items-center justify-center ${
        loading ? 'opacity-75 cursor-not-allowed' : ''
      } ${getButtonStyle()}`}
    >
      {getButtonText()}
      {isMainCTA && <ArrowRight className="ml-2 h-5 w-5" />}
    </button>
  );
};

export default SubscriptionHandler;